import { Injectable, inject } from '@angular/core';
import type { dateRangeDto, EchartsConfig, LaneConfig, ZoomSliderMode } from 'commons';
import type { DataZoomComponentOption, EChartsOption, GridComponentOption, XAXisComponentOption, YAXisComponentOption } from 'echarts';
import type { TitleOption } from 'echarts/types/dist/shared';
import type { EditmodeStateModel } from '../../../app/databrowser/lane-edit-mode-panel/editmode.types';
import type { CommentModel } from '../../../services/api/models/CommentModel';
import { formatTooltip } from '../classes/tooltip.class';
import type { DtisZoomEvent } from '../zoomEvent';
import { DataZoomBuilderService } from './data-zoom-builder.service';
import { CHART_CONSTANTS, type ChartConfigState } from './echarts-config.types';
import { GridBuilderService } from './grid-builder.service';
import type { AnomalyLaneIntervals } from './kpi-series-builder.service';
import { SeriesBuilderService } from './series-builder.service';
import { type TestPhaseInterval, TestPhaseMarkAreaBuilderService } from './test-phase-mark-area-builder.service';
import { TitleBuilderService } from './title-builder.service';
import { XAxisBuilderService } from './x-axis-builder.service';
import { YAxisBuilderService } from './y-axis-builder.service';

/**
 * Main service for generating ECharts configurations for multilane charts.
 * Orchestrates all builder services to create a complete chart configuration.
 */
@Injectable({ providedIn: 'root' })
export class EchartsConfigService {
  private readonly gridBuilder = inject(GridBuilderService);
  private readonly xAxisBuilder = inject(XAxisBuilderService);
  private readonly yAxisBuilder = inject(YAxisBuilderService);
  private readonly titleBuilder = inject(TitleBuilderService);
  private readonly dataZoomBuilder = inject(DataZoomBuilderService);
  private readonly seriesBuilder = inject(SeriesBuilderService);
  private readonly testPhaseMarkAreaBuilder = inject(TestPhaseMarkAreaBuilderService);

  /**
   * Generates a complete ECharts configuration for multilane charts.
   *
   * @param state - Current chart state including lanes, comments, KPI table, and zoom mode
   * @param zoom - Array of zoom events for maintaining zoom state
   * @param dateRange - Date range for the chart
   * @param editmode - Edit mode state for highlighting and panel selection
   * @param gridHeight - Optional custom grid height
   * @param config - Optional ECharts configuration overrides
   * @returns Complete ECharts configuration with series data
   */
  async generateConfig(
    state: ChartConfigState,
    zoom: DtisZoomEvent[],
    dateRange: dateRangeDto,
    editmode: Partial<EditmodeStateModel>,
    gridHeight: number = CHART_CONSTANTS.DEFAULT_GRID_HEIGHT,
    config?: EchartsConfig
  ): Promise<{ config: EChartsOption; series: unknown[] }> {
    const { laneConfig, zoomSliderMode, comments, kpiTable, testPhasesVisible = false, testPhases = [], commentsVisible = false } = state;

    // Build grid configuration
    const grid = this.gridBuilder.buildGrids(
      laneConfig,
      zoomSliderMode,
      gridHeight,

      config
    );

    // Build axis configurations
    const xAxis = this.xAxisBuilder.buildXAxes(laneConfig, dateRange, config);
    let yAxis = this.yAxisBuilder.buildYAxes(laneConfig, editmode.currentYAxisPanel ?? '', config);

    // Build series
    // Note: Comments are always fetched and stored in state when channel data loads.
    // We only pass them to the series builder when commentsVisible is true to control rendering.
    // This ensures instant toggle response without additional API calls.
    const series = await this.seriesBuilder.buildSeries(laneConfig, xAxis, yAxis, commentsVisible ? comments : [], editmode.highlightedComment, config);

    // Apply test phase mark areas from API-sourced phase data
    const testPhaseIntervals = this.applyTestPhaseMarkAreas(laneConfig, xAxis, series, testPhasesVisible, testPhases);

    // Apply confidence bands to series
    await this.seriesBuilder.applyConfidenceBands(laneConfig, series);

    // Inject KPI series
    const anomalyIntervals = this.seriesBuilder.injectKpiSeries(kpiTable, laneConfig, yAxis, series);

    // Apply Y-axis boundaries based on series data
    yAxis = await this.yAxisBuilder.applyBoundaries(laneConfig, series, yAxis, zoom);

    // Update X-axis boundaries
    const updatedXAxis = await this.applyXAxisBoundaries(xAxis, series, laneConfig, zoomSliderMode);

    // Build data zoom configuration
    const dataZoom = this.dataZoomBuilder.buildDataZoom(grid, updatedXAxis, yAxis, zoomSliderMode, zoom, config);

    // Build titles
    const titles = this.titleBuilder.buildTitles(laneConfig, zoomSliderMode, gridHeight, config);

    // Build tooltip with config
    const tooltip = this.createTooltip(comments, laneConfig, testPhaseIntervals, anomalyIntervals, config);

    // Build animation config
    const animationConfig = this.extractAnimationConfig(config);

    const chartConfig: EChartsOption = {
      grid,
      xAxis: updatedXAxis,
      yAxis,
      title: titles,
      dataZoom,
      tooltip,
      ...animationConfig,
    };

    return { config: chartConfig, series };
  }

  /**
   * Returns the base configuration without series data.
   * Useful for getting chart options without waiting for series loading.
   */
  getBaseConfig(
    grid: GridComponentOption[],
    axes: { xAxis: XAXisComponentOption[]; yAxis: YAXisComponentOption[] },
    titles: TitleOption | TitleOption[],
    dataZoom: DataZoomComponentOption[],
    laneData: { comments: CommentModel[]; laneConfig: LaneConfig[] },
    config?: EchartsConfig
  ): EChartsOption {
    return {
      grid,
      xAxis: axes.xAxis,
      yAxis: axes.yAxis,
      title: titles,
      dataZoom,
      tooltip: this.createTooltip(laneData.comments, laneData.laneConfig, [], [], config),
      ...this.extractAnimationConfig(config),
    };
  }

  private async applyXAxisBoundaries(
    xAxis: XAXisComponentOption[],
    series: unknown[],
    laneConfig: LaneConfig[],
    zoomSliderMode: ZoomSliderMode
  ): Promise<XAXisComponentOption[]> {
    const resolvedSeries = await Promise.all(series);

    const seriesXValues = resolvedSeries
      .flatMap((s: any) => s.data?.map((d: any[]) => d[0]))
      .filter(Number)
      .map(Number);

    return this.xAxisBuilder.applyBoundaries(xAxis, seriesXValues, laneConfig, zoomSliderMode);
  }

  private createTooltip(
    comments: CommentModel[],
    laneConfig: LaneConfig[],
    testPhaseIntervals: Array<{
      xAxisIndex: number;
      intervals: TestPhaseInterval[];
    }>,
    anomalyIntervals: AnomalyLaneIntervals[],
    config?: EchartsConfig
  ): EChartsOption['tooltip'] {
    return {
      show: config?.tooltipShow ?? true,
      trigger: config?.tooltipTrigger ?? 'axis',
      appendToBody: true,
      confine: true,
      axisPointer: {
        type: config?.tooltipAxisPointerType ?? 'cross',
        crossStyle: {
          color: config?.tooltipCrossStyleColor ?? '#999',
        },
      },
      backgroundColor: config?.tooltipBackgroundColor ?? 'rgba(255, 255, 255, 0.9)',
      borderColor: config?.tooltipBorderColor ?? '#ccc',
      borderWidth: config?.tooltipBorderWidth ?? 1,
      padding: config?.tooltipPadding ?? 10,
      textStyle: {
        color: config?.tooltipTextColor ?? '#333',
        fontSize: config?.tooltipFontSize ?? 12,
      },
      formatter: (params: unknown) => formatTooltip(params as any[], comments, laneConfig, testPhaseIntervals, anomalyIntervals),
    };
  }

  private extractAnimationConfig(config?: EchartsConfig): Partial<EChartsOption> {
    if (!config) {
      return {};
    }

    return {
      animation: config.animationEnabled ?? true,
      animationDuration: config.animationDuration ?? 1000,
      animationEasing: config.animationEasing ?? 'cubicOut',
      animationThreshold: config.animationThreshold ?? 2000,
      animationDelay: config.animationDelay ?? 0,
      animationDurationUpdate: config.animationDurationUpdate ?? 300,
      animationEasingUpdate: config.animationEasingUpdate ?? 'cubicOut',
      progressiveThreshold: config.progressiveThreshold ?? 3000,
    };
  }

  /**
   * Applies test phase mark areas to series from API-sourced phase data.
   * The mark areas are added to the first series in each lane.
   *
   * @param laneConfig - Array of lane configurations
   * @param xAxis - X-axis configurations for axis index mapping
   * @param series - Array of series to apply mark areas to
   * @param testPhasesVisible - Whether test phases should be visible
   * @param testPhases - API-sourced test phase items from state
   */
  private applyTestPhaseMarkAreas(
    laneConfig: LaneConfig[],
    xAxis: XAXisComponentOption[],
    series: unknown[],
    testPhasesVisible: boolean = false,
    testPhases: import('../../../app/states/test-phase').TestPhaseItem[] = []
  ): Array<{ xAxisIndex: number; intervals: TestPhaseInterval[] }> {
    // Only apply test phases if visibility is enabled and phases exist
    if (!testPhasesVisible || testPhases.length === 0) {
      return [];
    }

    // Build test phase mark areas from API data
    const testPhaseResults = this.testPhaseMarkAreaBuilder.buildTestPhaseMarkAreas(laneConfig, xAxis, testPhases);

    // Apply mark areas to the first series in each lane that has test phase data
    for (const result of testPhaseResults) {
      if (!result.hasData) {
        continue;
      }

      // Find the first series that belongs to the same lane (by xAxisIndex)
      const targetSeries = series.find((s: any) => s.xAxisIndex === result.xAxisIndex) as any;

      if (targetSeries) {
        // Merge test phase mark areas with existing mark areas (e.g., comments)
        const existingMarkArea = targetSeries.markArea;
        if (existingMarkArea && existingMarkArea.data?.length > 0) {
          // Combine existing mark areas with test phase mark areas
          targetSeries.markArea = {
            ...existingMarkArea,
            data: [...(result.markArea.data ?? []), ...existingMarkArea.data],
          };
        } else {
          targetSeries.markArea = result.markArea;
        }
      }
    }

    // Return intervals for all lanes with test phase data (for tooltip use)
    return testPhaseResults.filter((r) => r.hasData).map((r) => ({ xAxisIndex: r.xAxisIndex, intervals: r.intervals }));
  }
}
